import React, { useCallback, useEffect, useState } from 'react';

import { Box, Button, GoogleIcon, Text } from '@opendoor/bricks-next';
import { type EventData, globalObservability } from '@opendoor/observability/slim';

import {
  type AuthErrorCode,
  getAuthErrorMessage,
  normalizeAuthErrorCode,
  readRawAuthErrorFromLocation,
  stripAuthErrorFromUrl,
} from './authError';

type RedirectInitiatedEvent = EventData<'authentication', 'redirectInitiated'>;
const GOOGLE_AUTH_METHOD: RedirectInitiatedEvent['authMethod'] = 3;
const WEB_AUTH_SOURCE: RedirectInitiatedEvent['authSource'] = 0;
const WEB_PLATFORM: RedirectInitiatedEvent['platform'] = 1;

export type GoogleRedirectIntent = 'reauth';

export interface GoogleAuthRedirectButtonProps {
  analyticsPrefix: string;
  /** Optional post-login destination path. Passed as ?redirect_to= query param. */
  redirectTo?: string;
  /**
   * Optional server-side intent. When set to `'reauth'`, the button forwards
   * `?redirect_intent=reauth` to `/auth/google` so Rails treats the round-trip
   * as a session reauth (must match `current_user`) rather than a fresh login.
   * When omitted and no `redirectTo` is given, reauth callers default
   * `redirect_to` to the current page URL so the user lands back where they
   * were.
   */
  redirectIntent?: GoogleRedirectIntent;
  /** Button label text. Defaults to "Continue with Google" */
  label?: string;
  /** Button size. Defaults to "medium" */
  size?: 'xsmall' | 'small' | 'medium' | 'large';
  /** Button variant. Defaults to "secondary" */
  variant?: 'primary' | 'secondary' | 'secondaryInverse';
  /**
   * When true, the button stretches to fill its container with no maxWidth
   * cap. Defaults to false (content-sized button in a 400px-max wrapper),
   * matching existing redirect-button call sites.
   */
  fullWidth?: boolean;
  /** Optional callback fired before navigation. Awaited if it returns a promise. */
  onBeforeNavigate?: () => void | Promise<void>;
  /** Optional error handler if onBeforeNavigate throws. Receives the caught error. Throw from this handler to block navigation. Awaited if it returns a promise. */
  onBeforeNavigateError?: (error: unknown) => void | Promise<void>;
}

/**
 * Redirect-based Google auth button for the server-side OAuth flow.
 * Navigates to /auth/google — Rails handles the full OAuth exchange via OmniAuth.
 * No Google JavaScript SDK in the login path.
 */
export const GoogleAuthRedirectButton: React.FC<GoogleAuthRedirectButtonProps> = ({
  analyticsPrefix,
  redirectTo,
  redirectIntent,
  label = 'Continue with Google',
  size = 'medium',
  variant = 'secondary',
  fullWidth = false,
  onBeforeNavigate,
  onBeforeNavigateError,
}) => {
  const handlePress = useGoogleRedirectHandler({
    redirectTo,
    redirectIntent,
    onBeforeNavigate,
    onBeforeNavigateError,
  });
  const authErrorCode = useAuthErrorOnMount();
  const iconSize = size === 'xsmall' || size === 'small' ? 20 : 24;

  return (
    <Box width="100%" maxWidth={fullWidth ? undefined : 400}>
      <Box position="relative">
        <Button
          analyticsName={`${analyticsPrefix}-google-redirect-button`}
          testID="google-auth-redirect-button"
          label={label}
          size={size}
          variant={variant}
          onPress={handlePress}
          {...(fullWidth ? { width: '100%' } : {})}
        />
        <Box
          position="absolute"
          left="$12x"
          top="50%"
          style={{ transform: [{ translateY: '-50%' }] }}
          pointerEvents="none"
        >
          <GoogleIcon size={iconSize} />
        </Box>
      </Box>
      {authErrorCode ? (
        <Box paddingTop="$4x" role="alert" aria-live="polite" testID="google-auth-error-message">
          <Text tag="span" typography="$bodySmall" color="$contentStateErrorDefault">
            {getAuthErrorMessage(authErrorCode)}
          </Text>
        </Box>
      ) : null}
    </Box>
  );
};

/** Returns the path if it's a safe relative path, or undefined if it's absolute/protocol-relative/scheme-based. */
export function sanitizeRedirectPath(path: string | undefined | null): string | undefined {
  if (!path) return undefined;
  if (/^\/(?!\/)/.test(path)) return path;
  console.error(`[GoogleAuthRedirectButton] rejected non-relative redirect path: ${path}`);
  return undefined;
}

function navigateToGoogleAuth(redirectTo?: string, redirectIntent?: GoogleRedirectIntent) {
  const url = new URL('/auth/google', window.location.origin);
  const currentParams = new URLSearchParams(window.location.search);

  const redirectFromParams = currentParams.get('redirect_to');
  currentParams.delete('redirect_to');

  currentParams.forEach((value, key) => {
    url.searchParams.set(key, value);
  });

  // Build the reauth fallback from the redirect_to-stripped param set so an
  // inbound `?redirect_to=...` can't smuggle a nested target into the value
  // we hand Rails. sanitizeRedirectPath only inspects the path prefix, so a
  // raw window.location.search would let an unsafe inner `redirect_to=` ride
  // through into session['original_url'].
  const preservedSearch = currentParams.toString();
  const reauthDefaultRedirect =
    redirectIntent === 'reauth'
      ? `${window.location.pathname}${preservedSearch ? `?${preservedSearch}` : ''}`
      : undefined;

  // Reauth must always resolve to the current-page default when an explicit
  // `redirectTo` fails sanitization — never to inbound `redirect_to` (stripped
  // above for reauth) or `undefined`. Preserves the "land back where you were"
  // guarantee for any reauth caller that passes a malformed `redirectTo`.
  const safeRedirect =
    redirectIntent === 'reauth'
      ? (sanitizeRedirectPath(redirectTo) ?? sanitizeRedirectPath(reauthDefaultRedirect))
      : (sanitizeRedirectPath(redirectTo) ?? sanitizeRedirectPath(redirectFromParams));
  if (safeRedirect) {
    url.searchParams.set('redirect_to', safeRedirect);
  }

  if (redirectIntent) {
    url.searchParams.set('redirect_intent', redirectIntent);
  }

  window.location.href = url.pathname + url.search;
}

function trackGoogleRedirectInitiated() {
  globalObservability.trackJourneyEvent(
    'authentication',
    'redirectInitiated',
    {
      authMethod: GOOGLE_AUTH_METHOD,
      authSource: WEB_AUTH_SOURCE,
      platform: WEB_PLATFORM,
    },
    undefined,
    { flush: true },
  );
}

function reportRedirectInitiatedTrackingError(error: unknown) {
  try {
    globalObservability.trackError(
      'google-auth-redirect-initiated-journey-event-failed',
      error instanceof Error ? error : new Error(String(error)),
    );
  } catch {
    // Auth navigation should continue even if error reporting is unavailable.
  }
}

function useGoogleRedirectHandler({
  redirectTo,
  redirectIntent,
  onBeforeNavigate,
  onBeforeNavigateError,
}: Pick<
  GoogleAuthRedirectButtonProps,
  'redirectTo' | 'redirectIntent' | 'onBeforeNavigate' | 'onBeforeNavigateError'
>) {
  return useCallback(async () => {
    try {
      trackGoogleRedirectInitiated();
    } catch (e) {
      reportRedirectInitiatedTrackingError(e);
    }

    try {
      await onBeforeNavigate?.();
    } catch (e) {
      await onBeforeNavigateError?.(e);
    }

    // Flush buffered journey events before navigating away.
    // send() uses keepalive: true so the browser completes the request even during unload.
    globalObservability.getJourneyEventClient().send?.();

    navigateToGoogleAuth(redirectTo, redirectIntent);
  }, [redirectTo, redirectIntent, onBeforeNavigate, onBeforeNavigateError]);
}

/**
 * Reads `?auth_error=` once on mount, fires telemetry, strips the param. Returns
 * the normalized code for inline rendering, or null when no error is present.
 */
function useAuthErrorOnMount(): AuthErrorCode | null {
  const [code, setCode] = useState<AuthErrorCode | null>(null);

  useEffect(() => {
    const raw = readRawAuthErrorFromLocation();
    if (raw === null) return;

    const normalized = normalizeAuthErrorCode(raw);
    setCode(normalized);
    stripAuthErrorFromUrl();
    reportAuthErrorDisplayed(normalized, raw);
  }, []);

  return code;
}

const AUTH_ERROR_DISPLAYED_MESSAGE = 'Google OAuth redirect error displayed';
// Strict allowlist for raw auth_error values forwarded to telemetry. Only
// short, code-shaped strings are emitted verbatim; anything else (e.g. emails
// embedded in malformed values) is dropped to prevent accidental PII leakage.
const SAFE_RAW_AUTH_ERROR_PATTERN = /^[a-z0-9_-]{1,64}$/;

function reportAuthErrorDisplayed(normalized: AuthErrorCode, raw: string): void {
  try {
    const data: Record<string, unknown> = {
      authMethod: GOOGLE_AUTH_METHOD,
      platform: WEB_PLATFORM,
      oauthErrorCode: normalized,
    };
    if (raw !== normalized) {
      const trimmedRaw = raw.trim().toLowerCase();
      if (SAFE_RAW_AUTH_ERROR_PATTERN.test(trimmedRaw)) {
        data.oauthErrorRawCode = trimmedRaw;
      }
    }

    globalObservability.trackError(
      'google-auth-redirect-error-displayed',
      new Error(AUTH_ERROR_DISPLAYED_MESSAGE),
      { data },
    );
  } catch {
    // Telemetry must not break the inline error UI.
  }
}
